import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { vehicleApi } from '../../services/vehicleApi';

export interface Vehicle {
    id: number;
    user_id: number;
    branch_id: number;
    type_of_vehicle?: string;
    vehicle_number: string;
    make?: string;
    model?: string;
    unladen_weight?: string;
    capacity_in_unit?: string;
    capacity_in_weight?: string;
    capacity_in_volume?: string;
    chasis_number?: string;
    engine_number?: string;
    marker_name?: string;
    registration_number?: string;
    puc_validity?: string;
    insurance_validity?: string;
    ownership?: string;
    rented_from?: string;
    rented_to?: string;
    owner_name?: string;
    transporter?: string;
    financer?: string;
    created_at: string;
    updated_at: string;
    branch?: {
        id: number;
        name: string;
        code: string;
    };
    user?: {
        id: number;
        name: string;
        email: string;
    };
}

export interface CreateVehicleData {
    branch_id: number;
    vehicle_number: string;
    type_of_vehicle?: string;
    make?: string;
    model?: string;
    unladen_weight?: string;
    capacity_in_unit?: string;
    capacity_in_weight?: string;
    capacity_in_volume?: string;
    chasis_number?: string;
    engine_number?: string;
    marker_name?: string;
    registration_number?: string;
    puc_validity?: string;
    insurance_validity?: string;
    ownership?: string;
    rented_from?: string;
    rented_to?: string;
    owner_name?: string;
    transporter?: string;
    financer?: string;
}

export interface UpdateVehicleData {
    branch_id?: number;
    vehicle_number?: string;
    type_of_vehicle?: string;
    make?: string;
    model?: string;
    unladen_weight?: string;
    capacity_in_unit?: string;
    capacity_in_weight?: string;
    capacity_in_volume?: string;
    chasis_number?: string;
    engine_number?: string;
    marker_name?: string;
    registration_number?: string;
    puc_validity?: string;
    insurance_validity?: string;
    ownership?: string;
    rented_from?: string;
    rented_to?: string;
    owner_name?: string;
    transporter?: string;
    financer?: string;
}

export interface VehicleFilters {
    query?: string;
    branch_id?: number;
    type_of_vehicle?: string;
    ownership?: string;
    status?: string;
}

export interface VehicleResponse {
    vehicles: Vehicle[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface Branch {
    id: number;
    name: string;
    code: string;
}

interface VehicleState {
    vehicles: Vehicle[];
    branches: Branch[];
    selectedVehicle: Vehicle | null;
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: VehicleFilters;
    loading: boolean;
    error: string | null;
}

const initialState: VehicleState = {
    vehicles: [],
    branches: [],
    selectedVehicle: null,
    pagination: null,
    filters: {},
    loading: false,
    error: null,
};

// Async thunks
export const getVehicles = createAsyncThunk(
    'vehicles/getVehicles',
    async (params?: { page?: number; per_page?: number; filters?: VehicleFilters }) => {
        const response = await vehicleApi.getVehicles(params);
        return response;
    }
);

export const getVehicle = createAsyncThunk(
    'vehicles/getVehicle',
    async (id: number) => {
        const response = await vehicleApi.getVehicle(id);
        return response;
    }
);

export const createVehicle = createAsyncThunk(
    'vehicles/createVehicle',
    async (data: CreateVehicleData) => {
        const response = await vehicleApi.createVehicle(data);
        return response;
    }
);

export const updateVehicle = createAsyncThunk(
    'vehicles/updateVehicle',
    async ({ id, data }: { id: number; data: UpdateVehicleData }) => {
        const response = await vehicleApi.updateVehicle(id, data);
        return response;
    }
);

export const deleteVehicle = createAsyncThunk(
    'vehicles/deleteVehicle',
    async (id: number) => {
        await vehicleApi.deleteVehicle(id);
        return id;
    }
);

export const getBranches = createAsyncThunk(
    'vehicles/getBranches',
    async () => {
        const response = await vehicleApi.getBranches();
        return response;
    }
);

const vehicleSlice = createSlice({
    name: 'vehicles',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<VehicleFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {};
        },
        setSelectedVehicle: (state, action: PayloadAction<Vehicle | null>) => {
            state.selectedVehicle = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // getVehicles
        builder
            .addCase(getVehicles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getVehicles.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicles = action.payload.vehicles;
                state.pagination = action.payload.pagination;
            })
            .addCase(getVehicles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch vehicles';
            });

        // getVehicle
        builder
            .addCase(getVehicle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedVehicle = action.payload.vehicle;
            })
            .addCase(getVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch vehicle';
            });

        // createVehicle
        builder
            .addCase(createVehicle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicles.unshift(action.payload.vehicle);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create vehicle';
            });

        // updateVehicle
        builder
            .addCase(updateVehicle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateVehicle.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.vehicles.findIndex(v => v.id === action.payload.vehicle.id);
                if (index !== -1) {
                    state.vehicles[index] = action.payload.vehicle;
                }
                if (state.selectedVehicle?.id === action.payload.vehicle.id) {
                    state.selectedVehicle = action.payload.vehicle;
                }
            })
            .addCase(updateVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update vehicle';
            });

        // deleteVehicle
        builder
            .addCase(deleteVehicle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteVehicle.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicles = state.vehicles.filter(v => v.id !== action.payload);
                if (state.selectedVehicle?.id === action.payload) {
                    state.selectedVehicle = null;
                }
                if (state.pagination) {
                    state.pagination.total -= 1;
                }
            })
            .addCase(deleteVehicle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete vehicle';
            });

        // getBranches
        builder
            .addCase(getBranches.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getBranches.fulfilled, (state, action) => {
                state.loading = false;
                state.branches = action.payload;
            })
            .addCase(getBranches.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch branches';
            });
    },
});

export const { setFilters, clearFilters, setSelectedVehicle, clearError } = vehicleSlice.actions;
export default vehicleSlice.reducer;
